import { useState, type FormEvent } from "react";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const decoy = (data.get("website_alt") as string | null)?.trim() ?? "";
    if (decoy) {
      setStatus("success");
      setMessage("订阅成功，新的悬疑档案会第一时间送到你的邮箱。");
      form.reset();
      return;
    }

    const email = (data.get("email") as string | null)?.trim() ?? "";
    if (!email) {
      setStatus("error");
      setMessage("请填写有效的邮箱地址。");
      return;
    }

    const payload = new URLSearchParams();
    payload.set("email", email);

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch("https://readdy.ai/api/form/dah9d6bp14a1h3mr6g60", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const okCode = parsed?.code === "OK";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || !okCode || isSpam) {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setMessage("订阅成功，新的悬疑档案会第一时间送到你的邮箱。");
      form.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，请检查网络后重新提交。");
    }
  };

  return (
    <form
      id="lieqi-subscribe-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full max-w-xl"
      noValidate
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="relative flex-1">
          <span className="sr-only">邮箱地址</span>
          <i className="ri-mail-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="留下邮箱，订阅每周档案"
            className="w-full rounded-md border border-background-700 bg-background-950 py-3 pl-10 pr-4 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/60"
          />
        </label>
        <input
          type="text"
          name="website_alt"
          className="field-decoy"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? "提交中…" : "立即订阅"}
        </button>
      </div>
      {message && (
        <p
          role="status"
          className={`mt-3 flex items-center gap-2 text-sm ${
            status === "success" ? "text-secondary-300" : "text-accent-300"
          }`}
        >
          <i className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
          {message}
        </p>
      )}
    </form>
  );
}